import './AppRoot.css';
import React, { Component } from 'react';

import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';

import MainRoute from './routes';


var colors = {
  mediumslateblue: { // purple 
    '50':  '#f5f8f9',
    '100': '#f0f5f7',
    '200': '#dadcf6',
    '300': '#c7bdf6',
    '400': '#ab8cf2',
    '500': '#7d54ea',
    '600': '#6f41e7',
    '700': '#5f35d8',
    '800': '#512cb0',
    '900': '#3a2787',
  },
  mediumorchid: { // fuschia
    '50':  '#f9f9f9',
    '100': '#f7f6f5',
    '200': '#f0dbee',
    '300': '#ebb5ec',
    '400': '#e67ee1',
    '500': '#db54d4',
    '600': '#c134bd',
    '700': '#a529a8',
    '800': '#872486',
    '900': '#5c1f63',
  },
  hotpink: { // pink
    '50':  '#fbfaf9',
    '100': '#faf6ef',
    '200': '#f9dcdb',
    '300': '#f7b5cb',
    '400': '#f77da8',
    '500': '#f55285',
    '600': '#e93159',
    '700': '#d2274f',
    '800': '#b12149',
    '900': '#801d38',
  },
  coral: { // orange1
    '50':  '#faf9f7',
    '100': '#faf5e7',
    '200': '#fae0ba',
    '300': '#f8c387',
    '400': '#f79551',
    '500': '#f66c2f',
    '600': '#e84618',
    '700': '#cf3718',
    '800': '#aa2c1e',
    '900': '#81241c',
  },
  darkorange: { // orange2
    '50':  '#faf8f6',
    '100': '#faf4e3',
    '200': '#f9e2a9',
    '300': '#f7c969',
    '400': '#f6a136',
    '500': '#f47a1c',
    '600': '#e4530d',
    '700': '#c7420e',
    '800': '#a03315',
    '900': '#7b2815',
  },
  chocolate: { // orange3
    '50':  '#faf8f6',
    '100': '#f9f4e3',
    '200': '#f8e4a6',
    '300': '#f6ce60',
    '400': '#f2aa2f',
    '500': '#ef8418',
    '600': '#da5e0c',
    '700': '#ba4a0c',
    '800': '#913912',
    '900': '#702c13',
  },
  forestgreen: { // green
    '50':  '#f5f6f6',
    '100': '#eff4ed',
    '200': '#e0ebcc',
    '300': '#c5e18a',
    '400': '#8bce54',
    '500': '#62b033',
    '600': '#47931f',
    '700': '#38781b',
    '800': '#2b581c',
    '900': '#29431c',
  },
  mediumseagreen: { // seagreen
    '50':  '#eff5f6',
    '100': '#e1f3f3',
    '200': '#b1eae8',
    '300': '#77e3cc',
    '400': '#2ed2ab',
    '500': '#16b88d',
    '600': '#139e75',
    '700': '#13855e',
    '800': '#126444',
    '900': '#154e3a',
  },
  cornflowerblue: { // lightblue
    '50':  '#f0f7f9',
    '100': '#e1f5f9',
    '200': '#a4e7f7',
    '300': '#6cd9f4',
    '400': '#2cbdee',
    '500': '#1498e8',
    '600': '#1279e2',
    '700': '#1462ce',
    '800': '#144b9b',
    '900': '#143d77',
  },
  dodgerblue: { // mediumblue
    '50':  '#f2f7f9',
    '100': '#e5f6fa',
    '200': '#ade5f9',
    '300': '#7cd3f8',
    '400': '#3cb1f5',
    '500': '#1e87f1',
    '600': '#1a66ed',
    '700': '#1b52df',
    '800': '#1b40b2',
    '900': '#18358a',
  },
  steelblue: {
    '50':  '#f2f7f8',
    '100': '#e7f5f7',
    '200': '#bde7f1',
    '300': '#8ed9e8',
    '400': '#49bfda',
    '500': '#279bca',
    '600': '#4682b4',
    '700': '#1f649f',
    '800': '#1c4c72',
    '900': '#1b3d58',
  },
}
colors.primary = colors.mediumslateblue;
colors.secondary = colors.cornflowerblue;
colors.primary.main = colors.primary[500];
colors.secondary.main = colors.secondary[500];

const theme = createTheme({

  // main typography size
  typography: {
    fontSize: 12,
    color: "black"
  },

  //color theme
  palette: {
    colors: colors,
    primary: {
      main: colors.primary.main,
      dark: '#00114a',
      contrastText: '#ffffff',
      text: "#000000"
    },
    secondary: {
      main: colors.secondary.main,
      light: '#eeeeee',
      contrastText: '#ffffff',
      text: "#444444"
    },
    nav: {
      backgroundColor: colors.steelblue[700], //'#1B639E',
      text: '#ffffff',
      fontWeight: '500',
      fontSize: "80%"
    },
    landing: {
      text: '#ffffff',
    },
    background: {
      paper: "#ffffff",
      default: "#E1E2E1"
    }
  },


  components: {
    // If we want to modify here is the place.
    MuiTypography: {
      styleOverrides: {
        h1: {
        },
        h2: {
        },
        h3: {
        },
        h4: {
        },
        h5: {
        },
        h6: {
        },
        subtitle1: {
        },
        subtitle2: {
        },
        body1: {
        },
        body2: {
        },
        caption: {
        },
        button: {
        },
        overline: {
        },
      }
    },

    //The text field has an input label and this controls the color swap and uppercase behavior
    MuiInputLabel: { 
      styleOverrides: {
        root: { 
          fontSize: '14px',
          "&.MuiInputLabel-shrink": { 
            textTransform: 'uppercase',
            fontWeight: 'bold',
            fontSize: '12px',
            color: '#05B2FF',
            paddingTop: '4px',
            //transform: 'translate(0px, 4px)',
          },
  
          "&.Mui-focused": {
            color: colors.primary.main,
          }
        }
      }
    },

    //This overrides the text field input font
    MuiInput: { 
      styleOverrides: {
        root: { 
          fontSize: '14px',
        }
      }
    },


    //This overrides size of the table pagination.  Otherwise it scrolls too in small viewport.
    MuiTablePagination: { 
      styleOverrides: {
        root: { 
          fontSize: '12px',
        },
        selectLabel: { 
          fontSize: '12px',
        },
        displayedRows: { 
          fontSize: '12px',
        }
      }
    },

    //KD: Overrides for table border (collapse -> separate so that the cells don't try to apply bulk per cells.)
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate'
        }
      }
    },

    //SDW This is how you override default properties on all components.
    MuiButtonBase: {
      defaultProps: {
        // The props to change the default for.
        //disableRipple: true, // No more ripple!
      },
    },
    MuiSelect: {
      defaultProps: {
        // Make every select field a standard variant
        variant: 'standard'
      }
    },
    //this changes the font size of the mui select (hopefully not others too - needs more research)
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "14px"
        }
      }
    },


    MuiTextField: {
      defaultProps: {
        // Make every text field a standard variant
        variant: 'standard'
      },
      styleOverrides: {
        root: {
          width: "100%"
        }
      }
    },
    //this along with the above makes a field stretch the entire width of a grid item size
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: "100%"
        }
      }
    },

    MuiButton: {
      
      styleOverrides: {
        root: {
          // needed to override the font size on all buttons
          fontSize: '0.75rem',
        },

        outlinedSecondary: {
          borderWidth: '1px',
          fontWeight: 'normal',
          '&:hover': {
            borderColor: colors.secondary['700']
          }
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: colors.primary['700']
          }
        },
        outlinedPrimary: {
          "&:hover": {
            borderColor: colors.primary['700'],
            color: colors.primary['700'],
          }
        },
        containedSecondary: {
          '&:hover' : {
            backgroundColor: colors.secondary['700'],
          }
        }

      },
    },
    
    MuiStepIcon: {
      styleOverrides: {
        root: { 
          "&.Mui-completed": { 
            color: colors.secondary.main
          },
  
          "&.Mui-active": {
            color: colors.secondary.main,
          }
        }
      }
    },
    
    MuiTooltip: {
      styleOverrides: {
          tooltip: {
              fontSize: '0.75rem'
          }
      }
    },
    
    MuiLink: {
      styleOverrides: {
        root : {
          color: 'black'
        }
      }
    }
  },

});

class AppRoot extends Component {


  render = (props) => {
    return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <MainRoute/>
          </DndProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </LocalizationProvider> 
    );
  }
}

export default (AppRoot);
